<template>
  <div class="home">
    <NavBar />
    
    <!-- 英雄区域 -->
    <section class="hero">
      <div class="hero-bg-animation"></div>
      <div class="container">
        <div class="hero-content">
          <div class="hero-badge">
            <i class="fas fa-star"></i>
            <span>全新一代中文语义理解平台</span>
          </div>
          <h1 class="hero-title">中国语义理解<span class="highlight">新时代</span></h1>
          <h2 class="hero-subtitle">Chinese Language Foundation Platform</h2>
          <p class="hero-description">
            让中文世界的每一次表达，都被精准理解。<br>
            一个集合 <strong>NLP + LLM + 大规模中文知识库</strong> 的新一代语言基础平台，专为企业级应用场景而设计。
          </p>
          <div class="hero-stats">
            <div class="stat-item">
              <div class="stat-number">20+</div>
              <div class="stat-label">行业覆盖</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">100万+</div>
              <div class="stat-label">高质量语料</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">99.9%</div>
              <div class="stat-label">准确率</div>
            </div>
          </div>
          <div class="hero-buttons">
            <el-button type="primary" size="large" class="cta-button">
              <i class="fas fa-rocket"></i> 立即体验平台
            </el-button>
            <el-button size="large" class="cta-button-outline">
              <i class="fas fa-comments"></i> 与我们沟通合作
            </el-button>
          </div>
        </div>
        <div class="hero-image">
          <div class="hero-image-wrapper">
            <img src="../assets/images/hero-illustration.svg" alt="汉语基础平台" />
            <div class="hero-image-decoration"></div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 平台使命 -->
    <section id="mission" class="section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">平台使命</h2>
          <p class="section-subtitle">构建下一代中文语言基础设施</p>
          <div class="section-description">
            在中文语境中，企业在情感分析、分类、去噪、对齐、合规、知识抽取、多模态语义等任务上常常面临：
          </div>
        </div>
        
        <div class="mission-challenges">
          <div class="challenge-card">
            <div class="challenge-icon">
              <i class="fas fa-brain"></i>
            </div>
            <div class="challenge-content">
              <h3>LLM"太懂语言但不够可控"</h3>
              <p>大语言模型虽然理解能力强，但输出结果不可控，难以满足企业级应用的精确需求</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <i class="fas fa-microchip"></i>
            </div>
            <div class="challenge-content">
              <h3>传统 NLP "太可控但不够智能"</h3>
              <p>传统自然语言处理方法结果可控，但缺乏上下文理解能力，难以适应复杂场景</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <i class="fas fa-database"></i>
            </div>
            <div class="challenge-content">
              <h3>语料不统一、标准不同、语义质量参差</h3>
              <p>缺乏统一的中文语料标准和质量评估体系，难以积累和复用语言能力资产</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="challenge-content">
              <h3>难以积累自己的语言能力资产</h3>
              <p>企业难以构建属于自己的语言能力库，无法形成可持续的技术竞争力</p>
            </div>
          </div>
        </div>
        
        <div class="mission-solution">
          <div class="solution-icon">
            <i class="fas fa-lightbulb"></i>
          </div>
          <div class="solution-content">
            <h3>汉语基础平台的使命</h3>
            <p>让企业拥有一套：<strong>可控、可解释、统一标准、可扩展的中文语言能力层</strong></p>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 核心价值主张 -->
    <section id="value" class="section bg-light">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">核心价值主张</h2>
          <p class="section-subtitle">Value Proposition</p>
        </div>
        
        <div class="value-cards">
          <div class="value-card">
            <div class="value-icon">
              <i class="fas fa-layer-group"></i>
            </div>
            <div class="value-content">
              <h3>统一的中文语义基础设施</h3>
              <p>词级、短语级、句级、段落级、知识级全覆盖</p>
            </div>
          </div>
          
          <div class="value-card">
            <div class="value-icon">
              <i class="fas fa-project-diagram"></i>
            </div>
            <div class="value-content">
              <h3>NLP × LLM 的"混合编排"</h3>
              <p>在智能与可控之间取最佳平衡</p>
            </div>
          </div>
          
          <div class="value-card">
            <div class="value-icon">
              <i class="fas fa-shield-alt"></i>
            </div>
            <div class="value-content">
              <h3>企业级可控能力</h3>
              <p>禁用词、敏感词、否定词、火星文、繁简转换、词性、依存分析、自定义词库全面支持</p>
            </div>
          </div>
          
          <div class="value-card">
            <div class="value-icon">
              <i class="fas fa-award"></i>
            </div>
            <div class="value-content">
              <h3>高质量中文语料体系</h3>
              <p>覆盖 20+ 行业、百万级高质量标签语料、支持行业定制</p>
            </div>
          </div>
          
          <div class="value-card">
            <div class="value-icon">
              <i class="fas fa-server"></i>
            </div>
            <div class="value-content">
              <h3>本地化部署、支持私有化</h3>
              <p>满足金融、政府、运营、生产等场景需求</p>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 核心能力矩阵 -->
    <section id="capabilities" class="section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">核心能力矩阵</h2>
          <p class="section-subtitle">Core Capabilities</p>
        </div>
        
        <div class="capability-groups">
          <div class="capability-group">
            <div class="group-header">
              <div class="group-icon">
                <i class="fas fa-language"></i>
              </div>
              <h3>基础语言能力层</h3>
            </div>
            <div class="capability-items">
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-cut"></i>
                </div>
                <div class="item-content">
                  <h4>中文分词</h4>
                  <p>可自定义词典 + 工具链</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-exchange-alt"></i>
                </div>
                <div class="item-content">
                  <h4>文本转换</h4>
                  <p>拼音映射、火星文映射、繁简转换</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-search-minus"></i>
                </div>
                <div class="item-content">
                  <h4>特殊词识别</h4>
                  <p>否定词、助词、语气词识别</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-book"></i>
                </div>
                <div class="item-content">
                  <h4>词库扩展</h4>
                  <p>同义词、反义词、近义短语库</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-tags"></i>
                </div>
                <div class="item-content">
                  <h4>实体词扩展</h4>
                  <p>品牌词、实体词、场景词扩展</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="capability-group">
            <div class="group-header">
              <div class="group-icon">
                <i class="fas fa-project-diagram"></i>
              </div>
              <h3>句法与结构化理解</h3>
            </div>
            <div class="capability-items">
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-sitemap"></i>
                </div>
                <div class="item-content">
                  <h4>依存句法分析</h4>
                  <p>解析句子成分间依存关系</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-theater-masks"></i>
                </div>
                <div class="item-content">
                  <h4>语义角色标注</h4>
                  <p>识别句子中各成分的语义角色</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-id-card"></i>
                </div>
                <div class="item-content">
                  <h4>实体识别</h4>
                  <p>NER - 命名实体识别</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-calendar-alt"></i>
                </div>
                <div class="item-content">
                  <h4>事件抽取</h4>
                  <p>EE - 从文本中抽取事件信息</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-heart"></i>
                </div>
                <div class="item-content">
                  <h4>情绪/情感识别</h4>
                  <p>全场景情感分析</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="capability-group">
            <div class="group-header">
              <div class="group-icon">
                <i class="fas fa-brain"></i>
              </div>
              <h3>高级语义理解</h3>
            </div>
            <div class="capability-items">
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-comments"></i>
                </div>
                <div class="item-content">
                  <h4>对话意图识别</h4>
                  <p>理解用户对话的真实意图</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-link"></i>
                </div>
                <div class="item-content">
                  <h4>指代消解</h4>
                  <p>解决文本中的指代关系</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-network-wired"></i>
                </div>
                <div class="item-content">
                  <h4>知识图谱链接</h4>
                  <p>Entity Linking - 实体链接</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-vector-square"></i>
                </div>
                <div class="item-content">
                  <h4>语义相似度向量</h4>
                  <p>Text Embedding - 文本向量化</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-chart-bar"></i>
                </div>
                <div class="item-content">
                  <h4>结构化抽取</h4>
                  <p>指标/数据从文本中结构化抽取</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="capability-group">
            <div class="group-header">
              <div class="group-icon">
                <i class="fas fa-robot"></i>
              </div>
              <h3>LLM 语义增强能力</h3>
            </div>
            <div class="capability-items">
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="item-content">
                  <h4>LLM 纠错增强</h4>
                  <p>Error Correction - 文本纠错</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-question-circle"></i>
                </div>
                <div class="item-content">
                  <h4>LLM 解释增强</h4>
                  <p>Explainable NLP - 可解释NLP</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-shield-alt"></i>
                </div>
                <div class="item-content">
                  <h4>LLM 输出可控</h4>
                  <p>安全、合规、可审计</p>
                </div>
              </div>
              <div class="capability-item">
                <div class="item-icon">
                  <i class="fas fa-plug"></i>
                </div>
                <div class="item-content">
                  <h4>LLM 知识运算</h4>
                  <p>作为"知识运算"插件</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 应用场景 -->
    <section id="scenarios" class="section bg-light">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">典型应用场景</h2>
          <p class="section-subtitle">Use Cases</p>
        </div>
        
        <div class="scenario-cards">
          <div class="scenario-card">
            <div class="scenario-icon">
              <i class="fas fa-graduation-cap"></i>
            </div>
            <div class="scenario-content">
              <h3>教育行业</h3>
              <p>作文批改、知识点抽取、阅读理解结构化</p>
              <div class="scenario-tags">
                <span class="tag">智能批改</span>
                <span class="tag">知识图谱</span>
                <span class="tag">学习分析</span>
              </div>
            </div>
          </div>
          
          <div class="scenario-card">
            <div class="scenario-icon">
              <i class="fas fa-tools"></i>
            </div>
            <div class="scenario-content">
              <h3>运维监控</h3>
              <p>告警归因、事件聚类、根因分析（AIOps）</p>
              <div class="scenario-tags">
                <span class="tag">故障诊断</span>
                <span class="tag">预测分析</span>
                <span class="tag">自动化</span>
              </div>
            </div>
          </div>
          
          <div class="scenario-card">
            <div class="scenario-icon">
              <i class="fas fa-headset"></i>
            </div>
            <div class="scenario-content">
              <h3>客服与智能对话</h3>
              <p>意图识别、情绪判断、敏感词过滤、知识回答匹配</p>
              <div class="scenario-tags">
                <span class="tag">智能客服</span>
                <span class="tag">情感分析</span>
                <span class="tag">知识库</span>
              </div>
            </div>
          </div>
          
          <div class="scenario-card">
            <div class="scenario-icon">
              <i class="fas fa-newspaper"></i>
            </div>
            <div class="scenario-content">
              <h3>内容行业</h3>
              <p>敏感审核、文本去重、标签体系、推荐系统增强</p>
              <div class="scenario-tags">
                <span class="tag">内容审核</span>
                <span class="tag">去重检测</span>
                <span class="tag">推荐算法</span>
              </div>
            </div>
          </div>
          
          <div class="scenario-card">
            <div class="scenario-icon">
              <i class="fas fa-university"></i>
            </div>
            <div class="scenario-content">
              <h3>金融与政企</h3>
              <p>合规文本审查、表述标准统一、结构化入库</p>
              <div class="scenario-tags">
                <span class="tag">合规审查</span>
                <span class="tag">风险控制</span>
                <span class="tag">数据治理</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 技术架构 -->
    <section id="architecture" class="section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">技术架构</h2>
          <p class="section-subtitle">Tech Architecture</p>
        </div>
        
        <div class="architecture-diagram">
          <div class="arch-flow">
            <div class="arch-layer application-layer">
              <div class="layer-icon">
                <i class="fas fa-rocket"></i>
              </div>
              <div class="layer-content">
                <div class="layer-title">应用层</div>
                <div class="layer-desc">搜索/问答/审核/分析/推荐</div>
              </div>
            </div>
            
            <div class="flow-connector">
              <i class="fas fa-arrow-down"></i>
            </div>
            
            <div class="arch-layer llm-layer">
              <div class="layer-icon">
                <i class="fas fa-brain"></i>
              </div>
              <div class="layer-content">
                <div class="layer-title">LLM 推理层</div>
                <div class="layer-desc">智能理解与生成</div>
              </div>
            </div>
            
            <div class="flow-connector">
              <i class="fas fa-arrow-down"></i>
            </div>
            
            <div class="arch-layer nlp-layer">
              <div class="layer-icon">
                <i class="fas fa-project-diagram"></i>
              </div>
              <div class="layer-content">
                <div class="layer-title">NLP 核心理解层</div>
                <div class="layer-desc">语义分析与结构化</div>
              </div>
            </div>
            
            <div class="flow-connector">
              <i class="fas fa-arrow-down"></i>
            </div>
            
            <div class="arch-layer chinese-layer">
              <div class="layer-icon">
                <i class="fas fa-language"></i>
              </div>
              <div class="layer-content">
                <div class="layer-title">中文语言基础层</div>
                <div class="layer-desc">词级/短语级/语义规则/映射库</div>
              </div>
            </div>
            
            <div class="flow-connector">
              <i class="fas fa-arrow-down"></i>
            </div>
            
            <div class="arch-layer data-layer">
              <div class="layer-icon">
                <i class="fas fa-database"></i>
              </div>
              <div class="layer-content">
                <div class="layer-title">统一数据层</div>
                <div class="layer-desc">语料、标签、知识库</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 数据质量体系 -->
    <section class="section bg-light">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">数据质量体系</h2>
          <p class="section-subtitle">Data & Quality</p>
        </div>
        
        <div class="quality-points">
          <div class="quality-point">
            <div class="quality-icon">
              <i class="fas fa-database"></i>
            </div>
            <div class="quality-content">
              <h3>数据来源 10+ 类场景</h3>
              <p>覆盖新闻、社交、客服、教育、金融等多个领域的真实场景数据</p>
              <div class="quality-metrics">
                <span class="metric">1000万+ 语料</span>
                <span class="metric">10+ 行业覆盖</span>
              </div>
            </div>
          </div>
          
          <div class="quality-point">
            <div class="quality-icon">
              <i class="fas fa-tags"></i>
            </div>
            <div class="quality-content">
              <h3>五级语义标注体系</h3>
              <p>从基础词性到复杂语义关系的多层次标注，确保数据质量</p>
              <div class="quality-metrics">
                <span class="metric">5级标注</span>
                <span class="metric">多维度覆盖</span>
              </div>
            </div>
          </div>
          
          <div class="quality-point">
            <div class="quality-icon">
              <i class="fas fa-user-check"></i>
            </div>
            <div class="quality-content">
              <h3>专业 Linguist 质检流程</h3>
              <p>语言学专家参与标注和质检，确保语义理解的准确性</p>
              <div class="quality-metrics">
                <span class="metric">专家审核</span>
                <span class="metric">多重验证</span>
              </div>
            </div>
          </div>
          
          <div class="quality-point">
            <div class="quality-icon">
              <i class="fas fa-industry"></i>
            </div>
            <div class="quality-content">
              <h3>行业扩展模型</h3>
              <p>支持教育、AIOps、金融、客服等行业的定制化扩展</p>
              <div class="quality-metrics">
                <span class="metric">行业定制</span>
                <span class="metric">快速适配</span>
              </div>
            </div>
          </div>
          
          <div class="quality-point">
            <div class="quality-icon">
              <i class="fas fa-sync-alt"></i>
            </div>
            <div class="quality-content">
              <h3>持续学习与版本化管理</h3>
              <p>模型持续优化，数据版本化管理，确保系统稳定性</p>
              <div class="quality-metrics">
                <span class="metric">持续优化</span>
                <span class="metric">版本管理</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 为什么不是"只用 LLM" -->
    <section class="section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">为什么不是"只用 LLM"？</h2>
          <p class="section-subtitle">Why Not Only LLM</p>
        </div>
        
        <div class="llm-comparison">
          <div class="comparison-card traditional">
            <div class="card-icon">
              <i class="fas fa-cogs"></i>
            </div>
            <div class="card-content">
              <h3>传统 NLP</h3>
              <p class="card-subtitle">可控但笨</p>
              <p class="card-desc">无法适应上下文、无法泛化</p>
              <div class="card-tags">
                <span class="tag">规则驱动</span>
                <span class="tag">高精度</span>
                <span class="tag">低灵活性</span>
              </div>
            </div>
          </div>
          
          <div class="comparison-card llm">
            <div class="card-icon">
              <i class="fas fa-brain"></i>
            </div>
            <div class="card-content">
              <h3>LLM</h3>
              <p class="card-subtitle">聪明但不可控</p>
              <p class="card-desc">有时懂，有时乱说，有时越界</p>
              <div class="card-tags">
                <span class="tag">高泛化</span>
                <span class="tag">低稳定性</span>
                <span class="tag">不可预测</span>
              </div>
            </div>
          </div>
          
          <div class="comparison-card highlight">
            <div class="card-icon">
              <i class="fas fa-rocket"></i>
            </div>
            <div class="card-content">
              <h3>汉语基础平台</h3>
              <p class="card-subtitle">二者合体的最佳解</p>
              <div class="solution-points">
                <div class="point">
                  <i class="fas fa-shield-alt"></i>
                  <span>传统 NLP 负责"可控"</span>
                </div>
                <div class="point">
                  <i class="fas fa-lightbulb"></i>
                  <span>LLM 负责"智能"</span>
                </div>
                <div class="point">
                  <i class="fas fa-layer-group"></i>
                  <span>平台负责"融合 + 管控 + 协同"</span>
                </div>
              </div>
              <div class="final-result">
                <strong>最终得到 可控的智能</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 平台模块 -->
    <section class="section bg-light">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">平台模块</h2>
          <p class="section-subtitle">Modules</p>
        </div>
        
        <div class="module-grid">
          <div class="module-item">
            <div class="module-icon">
              <i class="fas fa-book"></i>
            </div>
            <div class="module-content">
              <h3>词语管理中心</h3>
              <p>词库、敏感词、停用词、否定词、短语</p>
              <div class="module-tags">
                <span class="tag">词库管理</span>
                <span class="tag">敏感词过滤</span>
              </div>
            </div>
          </div>
          
          <div class="module-item">
            <div class="module-icon">
              <i class="fas fa-brain"></i>
            </div>
            <div class="module-content">
              <h3>语义理解中心</h3>
              <p>NER、情绪、相似度、事件抽取</p>
              <div class="module-tags">
                <span class="tag">命名实体识别</span>
                <span class="tag">情绪分析</span>
              </div>
            </div>
          </div>
          
          <div class="module-item">
            <div class="module-icon">
              <i class="fas fa-file-alt"></i>
            </div>
            <div class="module-content">
              <h3>语料平台</h3>
              <p>清洗、标注、采样、审核</p>
              <div class="module-tags">
                <span class="tag">数据清洗</span>
                <span class="tag">智能标注</span>
              </div>
            </div>
          </div>
          
          <div class="module-item">
            <div class="module-icon">
              <i class="fas fa-project-diagram"></i>
            </div>
            <div class="module-content">
              <h3>知识图谱中心</h3>
              <p>实体、关系、规则</p>
              <div class="module-tags">
                <span class="tag">实体关系</span>
                <span class="tag">知识推理</span>
              </div>
            </div>
          </div>
          
          <div class="module-item">
            <div class="module-icon">
              <i class="fas fa-robot"></i>
            </div>
            <div class="module-content">
              <h3>LLM 控制中心</h3>
              <p>PromptHub、规约、审计、风险防护</p>
              <div class="module-tags">
                <span class="tag">提示工程</span>
                <span class="tag">风险防护</span>
              </div>
            </div>
          </div>
          
          <div class="module-item">
            <div class="module-icon">
              <i class="fas fa-plug"></i>
            </div>
            <div class="module-content">
              <h3>开放 API</h3>
              <p>RESTful / gRPC / Webhook</p>
              <div class="module-tags">
                <span class="tag">RESTful</span>
                <span class="tag">gRPC</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 成功案例 -->
    <section id="cases" class="section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">成功案例</h2>
          <p class="section-subtitle">Case Highlights</p>
        </div>
        
        <div class="case-cards">
          <div class="case-card">
            <div class="case-icon">
              <i class="fas fa-university"></i>
            </div>
            <div class="case-content">
              <h3>金融机构</h3>
              <p>构建中文标准化词库 → 误判率下降 40%</p>
              <div class="case-metrics">
                <span class="metric">误判率 -40%</span>
                <span class="metric">准确率 +35%</span>
              </div>
            </div>
          </div>
          
          <div class="case-card">
            <div class="case-icon">
              <i class="fas fa-graduation-cap"></i>
            </div>
            <div class="case-content">
              <h3>教育大型平台</h3>
              <p>阅读理解抽取正确率 +31%</p>
              <div class="case-metrics">
                <span class="metric">正确率 +31%</span>
                <span class="metric">效率 +50%</span>
              </div>
            </div>
          </div>
          
          <div class="case-card">
            <div class="case-icon">
              <i class="fas fa-server"></i>
            </div>
            <div class="case-content">
              <h3>运维平台</h3>
              <p>告警聚类 + 自动根因推断 → MTTR 降低 48%</p>
              <div class="case-metrics">
                <span class="metric">MTTR -48%</span>
                <span class="metric">准确率 +42%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 价格方案 -->
    <section id="pricing" class="section bg-light">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">价格与版本</h2>
          <p class="section-subtitle">Pricing</p>
        </div>
        
        <div class="pricing-cards">
          <div class="pricing-card">
            <div class="pricing-header">
              <h3>Free</h3>
              <div class="price">免费</div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>基础 API</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>限量调用</span>
              </div>
            </div>
            <div class="pricing-action">
              <el-button>开始使用</el-button>
            </div>
          </div>
          
          <div class="pricing-card">
            <div class="pricing-header">
              <h3>Pro</h3>
              <div class="price">专业版</div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>全套 NLP 能力</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>中型公司适用</span>
              </div>
            </div>
            <div class="pricing-action">
              <el-button type="primary">立即升级</el-button>
            </div>
          </div>
          
          <div class="pricing-card">
            <div class="pricing-header">
              <h3>Enterprise</h3>
              <div class="price">企业版</div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>私有化部署</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>定制行业库</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>语料支撑</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>专属模型</span>
              </div>
            </div>
            <div class="pricing-action">
              <el-button type="primary">联系销售</el-button>
            </div>
          </div>
          
          <div class="pricing-card featured">
            <div class="pricing-badge">推荐</div>
            <div class="pricing-header">
              <h3>Ultimate</h3>
              <div class="price">旗舰版</div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>全模块功能</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>企业级数据中台打通</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>专属技术支持</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>定制化开发</span>
              </div>
            </div>
            <div class="pricing-action">
              <el-button type="primary">咨询方案</el-button>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 最终 CTA -->
    <section class="section cta-section">
      <div class="cta-bg-animation"></div>
      <div class="container">
        <div class="cta-content">
          <h2 class="section-title">让中文世界的每一次表达，都被精准理解</h2>
          <p class="cta-subtitle">加入我们，共同探索中文语义理解的无限可能</p>
          <div class="cta-buttons">
            <el-button type="primary" size="large" class="cta-primary-btn">
              <i class="fas fa-rocket"></i> 立即体验平台
            </el-button>
            <el-button size="large" class="cta-secondary-btn">
              <i class="fas fa-comments"></i> 与我们沟通合作
            </el-button>
          </div>
          <div class="cta-stats">
            <div class="cta-stat">
              <span class="stat-number">10,000+</span>
              <span class="stat-label">活跃用户</span>
            </div>
            <div class="cta-stat">
              <span class="stat-number">99.9%</span>
              <span class="stat-label">准确率</span>
            </div>
            <div class="cta-stat">
              <span class="stat-number">24/7</span>
              <span class="stat-label">技术支持</span>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <Footer />
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import Footer from '@/components/Footer.vue'

export default {
  name: 'Home',
  components: {
    NavBar,
    Footer
  }
}
</script>

<style scoped lang="scss">
  :root {
    // 现代化配色方案
    --primary-color: #4b6bfb;
    --secondary-color: #6366f1;
    --accent-color: #8b5cf6;
    --success-color: #10b981;
    --warning-color: #f59e0b;
    --danger-color: #ef4444;
    --info-color: #3b82f6;
    
    // 文本颜色
    --text-color: #1f2937;
    --text-light: #6b7280;
    --text-muted: #9ca3af;
    
    // 背景颜色
    --background-color: #f9fafb;
    --background-light: #ffffff;
    --background-dark: #f3f4f6;
    
    // 阴影
    --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    --shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --shadow-md: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --shadow-lg: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    --shadow-xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    
    // 过渡动画
    --transition-fast: 0.15s ease;
    --transition: 0.3s ease;
    --transition-slow: 0.5s ease;
  }

  .home {
    font-family: 'Inter', 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', '微软雅黑', Arial, sans-serif;
    color: var(--text-color);
    line-height: 1.6;
    overflow-x: hidden;
    padding-top: 70px; // 为固定导航栏留出空间
    background-color: var(--background-color);
  }
  
  // 动画关键帧
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.7;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes float {
    0% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
    100% {
      transform: translateY(0px);
    }
  }

  @keyframes countUp {
    from {
      opacity: 0;
      transform: scale(0.5);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes bgAnimation {
    0% {
      background: radial-gradient(circle at 20% 50%, rgba(75, 107, 251, 0.1) 0%, rgba(75, 107, 251, 0) 50%),
                  radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0) 50%),
                  radial-gradient(circle at 40% 20%, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0) 50%);
    }
    33% {
      background: radial-gradient(circle at 70% 30%, rgba(75, 107, 251, 0.1) 0%, rgba(75, 107, 251, 0) 50%),
                  radial-gradient(circle at 30% 70%, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0) 50%),
                  radial-gradient(circle at 60% 40%, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0) 50%);
    }
    66% {
      background: radial-gradient(circle at 40% 70%, rgba(75, 107, 251, 0.1) 0%, rgba(75, 107, 251, 0) 50%),
                  radial-gradient(circle at 60% 30%, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0) 50%),
                  radial-gradient(circle at 80% 60%, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0) 50%);
    }
    100% {
      background: radial-gradient(circle at 20% 50%, rgba(75, 107, 251, 0.1) 0%, rgba(75, 107, 251, 0) 50%),
                  radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0) 50%),
                  radial-gradient(circle at 40% 20%, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0) 50%);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  // 通用样式
  .section {
    padding: 100px 0;
    
    &.bg-light {
      background-color: var(--background-color);
    }
    
    &.bg-dark {
      background-color: var(--background-dark);
    }
    
    &.bg-gradient {
      background: linear-gradient(135deg, var(--background-color) 0%, var(--background-dark) 100%);
    }
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  // 按钮样式
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 500;
    border-radius: 8px;
    transition: all var(--transition);
    cursor: pointer;
    border: none;
    text-decoration: none;
    
    i {
      margin-right: 8px;
    }
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-lg);
    }
    
    &.btn-primary {
      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
      color: white;
      
      &:hover {
        box-shadow: 0 10px 20px rgba(75, 107, 251, 0.3);
      }
    }
    
    &.btn-outline {
      background: transparent;
      border: 2px solid var(--primary-color);
      color: var(--primary-color);
      
      &:hover {
        background: rgba(75, 107, 251, 0.1);
      }
    }
  }

  // 卡片样式
  .card {
    background: var(--background-light);
    border-radius: 16px;
    padding: 30px;
    box-shadow: var(--shadow);
    transition: all var(--transition);
    border: 1px solid rgba(75, 107, 251, 0.1);
    
    &:hover {
      transform: translateY(-8px);
      box-shadow: var(--shadow-xl);
    }
  }

  // 标签样式
  .tag {
    display: inline-block;
    padding: 6px 12px;
    background-color: rgba(75, 107, 251, 0.1);
    color: var(--primary-color);
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    transition: all var(--transition-fast);
    
    &:hover {
      background-color: var(--primary-color);
      color: white;
      transform: scale(1.05);
    }
  }

  // 图标样式
  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    
    &.icon-primary {
      color: var(--primary-color);
    }
    
    &.icon-secondary {
      color: var(--secondary-color);
    }
    
    &.icon-accent {
      color: var(--accent-color);
    }
    
    &.icon-success {
      color: var(--success-color);
    }
    
    &.icon-warning {
      color: var(--warning-color);
    }
    
    &.icon-danger {
      color: var(--danger-color);
    }
    
    &.icon-info {
      color: var(--info-color);
    }
  }

  // 文本样式
  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  .text-primary {
    color: var(--primary-color);
  }

  .text-secondary {
    color: var(--secondary-color);
  }

  .text-accent {
    color: var(--accent-color);
  }

  .text-success {
    color: var(--success-color);
  }

  .text-warning {
    color: var(--warning-color);
  }

  .text-danger {
    color: var(--danger-color);
  }

  .text-info {
    color: var(--info-color);
  }

  .text-light {
    color: var(--text-light);
  }

  .text-muted {
    color: var(--text-muted);
  }

  // 边距样式
  .mt-1 { margin-top: 0.25rem; }
  .mt-2 { margin-top: 0.5rem; }
  .mt-3 { margin-top: 1rem; }
  .mt-4 { margin-top: 1.5rem; }
  .mt-5 { margin-top: 3rem; }
  
  .mb-1 { margin-bottom: 0.25rem; }
  .mb-2 { margin-bottom: 0.5rem; }
  .mb-3 { margin-bottom: 1rem; }
  .mb-4 { margin-bottom: 1.5rem; }
  .mb-5 { margin-bottom: 3rem; }
  
  .ml-1 { margin-left: 0.25rem; }
  .ml-2 { margin-left: 0.5rem; }
  .ml-3 { margin-left: 1rem; }
  .ml-4 { margin-left: 1.5rem; }
  .ml-5 { margin-left: 3rem; }
  
  .mr-1 { margin-right: 0.25rem; }
  .mr-2 { margin-right: 0.5rem; }
  .mr-3 { margin-right: 1rem; }
  .mr-4 { margin-right: 1.5rem; }
  .mr-5 { margin-right: 3rem; }
  
  .pt-1 { padding-top: 0.25rem; }
  .pt-2 { padding-top: 0.5rem; }
  .pt-3 { padding-top: 1rem; }
  .pt-4 { padding-top: 1.5rem; }
  .pt-5 { padding-top: 3rem; }
  
  .pb-1 { padding-bottom: 0.25rem; }
  .pb-2 { padding-bottom: 0.5rem; }
  .pb-3 { padding-bottom: 1rem; }
  .pb-4 { padding-bottom: 1.5rem; }
  .pb-5 { padding-bottom: 3rem; }
  
  .pl-1 { padding-left: 0.25rem; }
  .pl-2 { padding-left: 0.5rem; }
  .pl-3 { padding-left: 1rem; }
  .pl-4 { padding-left: 1.5rem; }
  .pl-5 { padding-left: 3rem; }
  
  .pr-1 { padding-right: 0.25rem; }
  .pr-2 { padding-right: 0.5rem; }
  .pr-3 { padding-right: 1rem; }
  .pr-4 { padding-right: 1.5rem; }
  .pr-5 { padding-right: 3rem; }

// 英雄区域
.hero {
  position: relative;
  padding: 100px 0 80px;
  background: linear-gradient(135deg, var(--background-color) 0%, var(--background-dark) 100%);
  overflow: hidden;
  
  .hero-bg-animation {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 20% 50%, rgba(75, 107, 251, 0.1) 0%, rgba(75, 107, 251, 0) 50%),
                radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0) 50%),
                radial-gradient(circle at 40% 20%, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0) 50%);
    z-index: 0;
    animation: bgAnimation 20s ease infinite;
  }
  
  .container {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .hero-content {
    flex: 1;
    max-width: 600px;
    animation: fadeInUp 0.8s ease-out;
  }
  
  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(75, 107, 251, 0.1);
    color: var(--primary-color);
    padding: 6px 16px;
    border-radius: 30px;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 24px;
    border: 1px solid rgba(75, 107, 251, 0.2);
    animation: pulse 2s infinite;
    
    i {
      font-size: 12px;
    }
  }
  
  .hero-title {
    font-size: 56px;
    font-weight: 700;
    color: var(--text-color);
    margin-bottom: 10px;
    line-height: 1.2;
    
    .highlight {
      color: var(--primary-color);
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        bottom: 4px;
        left: 0;
        width: 100%;
        height: 8px;
        background-color: rgba(75, 107, 251, 0.2);
        z-index: -1;
        border-radius: 4px;
      }
    }
  }
  
  .hero-subtitle {
    font-size: 24px;
    color: var(--primary-color);
    margin-bottom: 20px;
    font-weight: 500;
    opacity: 0.9;
  }
  
  .hero-description {
    font-size: 18px;
    color: var(--text-light);
    margin-bottom: 30px;
    line-height: 1.6;
    
    strong {
      color: var(--primary-color);
      font-weight: 600;
    }
  }
  
  .hero-stats {
    display: flex;
    gap: 40px;
    margin-bottom: 40px;
    
    .stat-item {
      text-align: center;
      
      .stat-number {
        font-size: 32px;
        font-weight: 700;
        color: var(--primary-color);
        margin-bottom: 8px;
        animation: countUp 2s ease-out;
      }
      
      .stat-label {
        font-size: 14px;
        color: var(--text-light);
      }
    }
  }
  
  .hero-buttons {
    display: flex;
    gap: 20px;
    
    .cta-button {
      padding: 14px 32px;
      font-size: 16px;
      font-weight: 500;
      border-radius: 8px;
      transition: all var(--transition);
      
      i {
        margin-right: 8px;
      }
      
      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 10px 20px rgba(75, 107, 251, 0.3);
      }
    }
    
    .cta-button-outline {
      padding: 14px 32px;
      font-size: 16px;
      font-weight: 500;
      border-radius: 8px;
      border: 2px solid var(--primary-color);
      color: var(--primary-color);
      background: transparent;
      transition: all var(--transition);
      
      i {
        margin-right: 8px;
      }
      
      &:hover {
        background: rgba(75, 107, 251, 0.1);
        transform: translateY(-3px);
        box-shadow: 0 10px 20px rgba(75, 107, 251, 0.2);
      }
    }
  }
  
  .hero-image {
    flex: 1;
    text-align: center;
    animation: fadeInRight 1s ease-out;
    
    .hero-image-wrapper {
      position: relative;
      
      img {
        max-width: 100%;
        height: auto;
        filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1));
        animation: float 6s ease-in-out infinite;
      }
      
      .hero-image-decoration {
        position: absolute;
        top: -20px;
        right: -20px;
        width: 100px;
        height: 100px;
        background: linear-gradient(45deg, var(--primary-color), var(--accent-color));
        border-radius: 50%;
        opacity: 0.1;
        animation: pulse 3s infinite;
      }
    }
  }
}

// 背景色
.bg-light {
  background-color: var(--background-color);
}

// 区域标题样式
.section-header {
  text-align: center;
  margin-bottom: 60px;
  
  .section-title {
    font-size: 42px;
    font-weight: 700;
    color: var(--text-color);
    margin-bottom: 16px;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
      border-radius: 2px;
    }
  }
  
  .section-subtitle {
    font-size: 20px;
    color: var(--text-light);
    margin-top: 20px;
    font-weight: 500;
  }
  
  .section-description {
    font-size: 18px;
    color: var(--text-light);
    max-width: 800px;
    margin: 20px auto 0;
    line-height: 1.6;
  }
}

// 平台使命
.mission-challenges {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 60px;
}

.challenge-card {
  background: #fff;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
  
  .challenge-icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    background: linear-gradient(135deg, #409EFF, #67C23A);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    
    i {
      font-size: 24px;
      color: #fff;
    }
  }
  
  .challenge-content {
    flex: 1;
    
    h3 {
      font-size: 20px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 12px;
      line-height: 1.4;
    }
    
    p {
      font-size: 16px;
      color: #606266;
      line-height: 1.6;
    }
  }
}

.mission-solution {
  background: linear-gradient(135deg, #409EFF, #67C23A);
  border-radius: 20px;
  padding: 40px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 30px;
  box-shadow: 0 15px 35px rgba(64, 158, 255, 0.2);
  
  .solution-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    i {
      font-size: 36px;
      color: #fff;
    }
  }
  
  .solution-content {
    h3 {
      font-size: 28px;
      font-weight: 700;
      margin-bottom: 16px;
    }
    
    p {
      font-size: 18px;
      line-height: 1.6;
      opacity: 0.9;
      
      strong {
        font-weight: 600;
        color: #fff;
      }
    }
  }
}

// 核心价值主张
.value-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.value-card {
  background: #fff;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    
    .value-icon {
      transform: scale(1.1);
    }
  }
  
  .value-icon {
    width: 70px;
    height: 70px;
    border-radius: 14px;
    background: linear-gradient(135deg, #409EFF, #67C23A);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    transition: transform 0.3s ease;
    
    i {
      font-size: 28px;
      color: #fff;
    }
  }
  
  .value-content {
    flex: 1;
    
    h3 {
      font-size: 20px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 12px;
      line-height: 1.4;
    }
    
    p {
      font-size: 16px;
      color: #606266;
      line-height: 1.6;
    }
  }
}

// 核心能力矩阵
.capability-groups {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-top: 20px;
  
  .capability-group {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(75, 107, 251, 0.1);
    transition: all 0.3s ease;
    overflow: hidden;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
    }
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 12px 40px rgba(75, 107, 251, 0.15);
    }
    
    .group-header {
      display: flex;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      
      .group-icon {
        width: 50px;
        height: 50px;
        border-radius: 12px;
        background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 15px;
        box-shadow: 0 4px 15px rgba(75, 107, 251, 0.2);
        
        i {
          color: white;
          font-size: 24px;
        }
      }
      
      h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #333;
      }
    }
    
    .capability-items {
      .capability-item {
        display: flex;
        align-items: flex-start;
        padding: 12px 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.03);
        transition: all 0.2s ease;
        
        &:last-child {
          border-bottom: none;
        }
        
        &:hover {
          background-color: rgba(75, 107, 251, 0.03);
          border-radius: 8px;
          padding-left: 10px;
          padding-right: 10px;
        }
        
        .item-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background-color: rgba(75, 107, 251, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          flex-shrink: 0;
          transition: all 0.2s ease;
          
          i {
            color: var(--primary-color);
            font-size: 16px;
          }
        }
        
        &:hover .item-icon {
          background-color: var(--primary-color);
          
          i {
            color: white;
          }
        }
        
        .item-content {
          flex: 1;
          
          h4 {
            margin: 0 0 4px 0;
            font-size: 16px;
            font-weight: 600;
            color: #333;
          }
          
          p {
            margin: 0;
            font-size: 14px;
            color: #666;
            line-height: 1.4;
          }
        }
      }
    }
  }
}

// 应用场景样式
.scenario-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
}

.scenario-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(75, 107, 251, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(75, 107, 251, 0.15);
  }
  
  .scenario-icon {
    width: 70px;
    height: 70px;
    border-radius: 16px;
    background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    box-shadow: 0 8px 20px rgba(75, 107, 251, 0.2);
    
    i {
      color: white;
      font-size: 32px;
    }
  }
  
  .scenario-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    
    h3 {
      font-size: 22px;
      font-weight: 600;
      margin-bottom: 15px;
      color: #333;
    }
    
    p {
      color: #666;
      line-height: 1.6;
      margin-bottom: 20px;
      flex-grow: 1;
    }
    
    .scenario-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      
      .tag {
        display: inline-block;
        padding: 6px 12px;
        background-color: rgba(75, 107, 251, 0.1);
        color: var(--primary-color);
        border-radius: 20px;
        font-size: 12px;
        font-weight: 500;
        transition: all 0.2s ease;
        
        &:hover {
          background-color: var(--primary-color);
          color: white;
          transform: scale(1.05);
        }
      }
    }
  }
}

// 技术架构样式
.architecture-diagram {
  max-width: 900px;
  margin: 0 auto;
  
  .arch-flow {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .arch-layer {
    display: flex;
    align-items: center;
    padding: 24px;
    border-radius: 16px;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 6px;
      height: 100%;
      background: rgba(255, 255, 255, 0.3);
    }
    
    &:hover {
      transform: translateX(10px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    }
    
    .layer-icon {
      width: 60px;
      height: 60px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 20px;
      flex-shrink: 0;
      
      i {
        color: white;
        font-size: 28px;
      }
    }
    
    .layer-content {
      flex: 1;
      
      .layer-title {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 5px;
        color: white;
      }
      
      .layer-desc {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }
  
  .flow-connector {
    display: flex;
    justify-content: center;
    color: var(--primary-color);
    font-size: 24px;
    margin: 5px 0;
    
    i {
      animation: pulse 2s infinite;
    }
  }
  
  .application-layer {
    background: linear-gradient(135deg, #5e72e4 0%, #825ee4 100%);
  }
  
  .llm-layer {
    background: linear-gradient(135deg, #2dce89 0%, #2dcecc 100%);
  }
  
  .nlp-layer {
    background: linear-gradient(135deg, #fb6340 0%, #fbb140 100%);
  }
  
  .chinese-layer {
    background: linear-gradient(135deg, #11cdef 0%, #1171ef 100%);
  }
  
  .data-layer {
    background: linear-gradient(135deg, #f5365c 0%, #f56036 100%);
  }
}

// 数据质量体系样式
.quality-points {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
}

.quality-point {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(75, 107, 251, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: flex-start;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(75, 107, 251, 0.15);
  }
  
  .quality-icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 20px;
    flex-shrink: 0;
    box-shadow: 0 8px 20px rgba(75, 107, 251, 0.2);
    
    i {
      color: white;
      font-size: 28px;
    }
  }
  
  .quality-content {
    flex: 1;
    
    h3 {
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 15px;
      color: #333;
    }
    
    p {
      color: #666;
      line-height: 1.6;
      margin-bottom: 20px;
    }
    
    .quality-metrics {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      
      .metric {
        display: inline-block;
        padding: 6px 12px;
        background-color: rgba(75, 107, 251, 0.1);
        color: var(--primary-color);
        border-radius: 20px;
        font-size: 12px;
        font-weight: 500;
        transition: all 0.2s ease;
        
        &:hover {
          background-color: var(--primary-color);
          color: white;
          transform: scale(1.05);
        }
      }
    }
  }
}

// LLM 对比样式
.llm-comparison {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.comparison-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 16px;
  padding: 0;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(75, 107, 251, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(75, 107, 251, 0.15);
  }
  
  &.traditional {
    border-top: 4px solid #6c757d;
    
    .card-icon {
      background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
    }
    
    .tag {
      background-color: rgba(108, 117, 125, 0.1);
      color: #6c757d;
      
      &:hover {
        background-color: #6c757d;
        color: white;
      }
    }
  }
  
  &.llm {
    border-top: 4px solid #17a2b8;
    
    .card-icon {
      background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
    }
    
    .tag {
      background-color: rgba(23, 162, 184, 0.1);
      color: #17a2b8;
      
      &:hover {
        background-color: #17a2b8;
        color: white;
      }
    }
  }
  
  &.highlight {
    border: none;
    box-shadow: 0 10px 40px rgba(75, 107, 251, 0.2);
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
    }
    
    .card-icon {
      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
      box-shadow: 0 8px 20px rgba(75, 107, 251, 0.3);
    }
    
    .tag {
      background-color: rgba(255, 255, 255, 0.2);
      color: white;
      
      &:hover {
        background-color: rgba(255, 255, 255, 0.3);
      }
    }
    
    .final-result {
      background: rgba(255, 255, 255, 0.15);
      padding: 15px;
      border-radius: 8px;
      margin-top: 20px;
      text-align: center;
      font-size: 18px;
    }
  }
  
  .card-icon {
    width: 80px;
    height: 80px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 30px auto 20px;
    
    i {
      color: white;
      font-size: 36px;
    }
  }
  
  .card-content {
    padding: 0 30px 30px;
    text-align: center;
    
    h3 {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 10px;
      color: #333;
    }
    
    .card-subtitle {
      font-size: 18px;
      font-weight: 500;
      margin-bottom: 15px;
      color: #666;
    }
    
    .card-desc {
      color: #666;
      line-height: 1.6;
      margin-bottom: 20px;
    }
    
    .card-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: center;
      margin-bottom: 20px;
      
      .tag {
        display: inline-block;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 500;
        transition: all 0.2s ease;
        
        &:hover {
          transform: scale(1.05);
        }
      }
    }
    
    .solution-points {
      text-align: left;
      margin-bottom: 20px;
      
      .point {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        
        i {
          color: white;
          font-size: 16px;
          margin-right: 10px;
          opacity: 0.9;
        }
        
        span {
          color: rgba(255, 255, 255, 0.9);
          font-size: 16px;
        }
      }
    }
  }
}

// 平台模块样式
.module-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.module-item {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 16px;
  padding: 0;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(75, 107, 251, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(75, 107, 251, 0.15);
    
    .module-icon {
      transform: scale(1.1);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  }
  
  .module-icon {
    width: 70px;
    height: 70px;
    border-radius: 14px;
    background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 30px auto 20px;
    transition: all 0.3s ease;
    box-shadow: 0 8px 20px rgba(75, 107, 251, 0.2);
    
    i {
      color: white;
      font-size: 32px;
    }
  }
  
  .module-content {
    padding: 0 30px 30px;
    text-align: center;
    
    h3 {
      font-size: 22px;
      font-weight: 600;
      margin-bottom: 15px;
      color: #333;
    }
    
    p {
      color: #666;
      line-height: 1.6;
      margin-bottom: 20px;
    }
    
    .module-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: center;
      
      .tag {
        display: inline-block;
        padding: 6px 12px;
        background-color: rgba(75, 107, 251, 0.1);
        color: var(--primary-color);
        border-radius: 20px;
        font-size: 12px;
        font-weight: 500;
        transition: all 0.2s ease;
        
        &:hover {
          background-color: var(--primary-color);
          color: white;
          transform: scale(1.05);
        }
      }
    }
  }
}

// 成功案例
.case-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.case-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 16px;
  padding: 0;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(75, 107, 251, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(75, 107, 251, 0.15);
    
    .case-icon {
      transform: scale(1.1);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  }
  
  .case-icon {
    width: 70px;
    height: 70px;
    border-radius: 14px;
    background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 30px auto 20px;
    transition: all 0.3s ease;
    box-shadow: 0 8px 20px rgba(75, 107, 251, 0.2);
    
    i {
      color: white;
      font-size: 32px;
    }
  }
  
  .case-content {
    padding: 0 30px 30px;
    text-align: center;
    
    h3 {
      font-size: 22px;
      font-weight: 600;
      margin-bottom: 15px;
      color: #333;
    }
    
    p {
      color: #666;
      line-height: 1.6;
      margin-bottom: 20px;
    }
    
    .case-metrics {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      justify-content: center;
      
      .metric {
        display: inline-block;
        padding: 6px 12px;
        background-color: rgba(75, 107, 251, 0.1);
        color: var(--primary-color);
        border-radius: 20px;
        font-size: 12px;
        font-weight: 500;
        transition: all 0.2s ease;
        
        &:hover {
          background-color: var(--primary-color);
          color: white;
          transform: scale(1.05);
        }
      }
    }
  }
}

// 价格方案
.pricing-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.pricing-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 16px;
  padding: 0;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(75, 107, 251, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(75, 107, 251, 0.15);
  }
  
  &.featured {
    border: none;
    box-shadow: 0 10px 40px rgba(75, 107, 251, 0.2);
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
    }
    
    .pricing-header {
      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
      
      h3, .price {
        color: white;
      }
    }
    
    .pricing-badge {
      position: absolute;
      top: -12px;
      right: 20px;
      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
      color: white;
      padding: 5px 15px;
      border-radius: 20px;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 10px rgba(75, 107, 251, 0.3);
    }
  }
  
  .pricing-header {
    padding: 30px 30px 20px;
    text-align: center;
    background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
    border-bottom: 1px solid rgba(75, 107, 251, 0.1);
    
    h3 {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 10px;
      color: #333;
    }
    
    .price {
      font-size: 28px;
      font-weight: 600;
      color: var(--primary-color);
    }
  }
  
  .pricing-features {
    padding: 20px 30px;
    
    .feature-item {
      display: flex;
      align-items: center;
      margin-bottom: 15px;
      
      i {
        color: var(--primary-color);
        margin-right: 12px;
        font-size: 16px;
      }
      
      span {
        color: #666;
        font-size: 16px;
      }
    }
  }
  
  .pricing-action {
    padding: 0 30px 30px;
    text-align: center;
    
    .el-button {
      width: 100%;
      padding: 12px 0;
      font-weight: 500;
    }
  }
}

// CTA 区域
.cta-section {
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: white;
  text-align: center;
  padding: 100px 0;
  position: relative;
  overflow: hidden;
  
  .cta-bg-animation {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    animation: float 20s ease-in-out infinite;
    z-index: 0;
  }
  
  .container {
    position: relative;
    z-index: 1;
  }
  
  .cta-content {
    max-width: 800px;
    margin: 0 auto;
  }
  
  .section-title {
    color: white;
    margin-bottom: 20px;
    font-size: 42px;
    font-weight: 700;
    animation: fadeInUp 1s ease-out;
  }
  
  .cta-subtitle {
    font-size: 20px;
    margin-bottom: 40px;
    opacity: 0.9;
    animation: fadeInUp 1s ease-out 0.2s both;
  }
  
  .cta-buttons {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-bottom: 60px;
    animation: fadeInUp 1s ease-out 0.4s both;
    
    .cta-primary-btn {
      padding: 15px 30px;
      font-size: 18px;
      font-weight: 600;
      border-radius: 50px;
      background: white;
      color: var(--primary-color);
      border: none;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      transform: translateY(0);
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
      }
    }
    
    .cta-secondary-btn {
      padding: 15px 30px;
      font-size: 18px;
      font-weight: 600;
      border-radius: 50px;
      background-color: transparent;
      border: 2px solid white;
      color: white;
      transform: translateY(0);
      transition: all 0.3s ease;
      
      &:hover {
        background-color: white;
        color: var(--primary-color);
        transform: translateY(-5px);
      }
      
      i {
        margin-right: 8px;
      }
    }
  }
  
  .cta-stats {
    display: flex;
    justify-content: center;
    gap: 40px;
    animation: fadeInUp 1s ease-out 0.6s both;
    
    .cta-stat {
      display: flex;
      flex-direction: column;
      align-items: center;
      
      .stat-number {
        font-size: 32px;
        font-weight: 700;
        margin-bottom: 5px;
        color: white;
      }
      
      .stat-label {
        font-size: 16px;
        opacity: 0.8;
      }
    }
  }
}

// 响应式设计
// 大屏幕设备 (1200px 以上)
@media (min-width: 1200px) {
  .container {
    max-width: 1200px;
  }
  
  .hero-title {
    font-size: 56px;
  }
  
  .hero-subtitle {
    font-size: 24px;
  }
  
  .hero-image {
    width: 600px;
    height: 600px;
  }
}

// 中等屏幕设备 (992px - 1199px)
@media (max-width: 1199px) {
  .container {
    max-width: 960px;
  }
  
  .hero-title {
    font-size: 48px;
  }
  
  .hero-image {
    width: 500px;
    height: 500px;
  }
  
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }
  
  .capability-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .scenario-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

// 平板设备 (768px - 991px)
@media (max-width: 991px) {
  .container {
    max-width: 720px;
    padding: 0 20px;
  }
  
  .hero {
    padding: 100px 0 80px;
    
    .container {
      flex-direction: column;
      text-align: center;
    }
    
    .hero-content {
      margin-bottom: 40px;
    }
    
    .hero-title {
      font-size: 42px;
    }
    
    .hero-subtitle {
      font-size: 22px;
    }
    
    .hero-buttons {
      justify-content: center;
      flex-wrap: wrap;
    }
    
    .hero-image {
      width: 400px;
      height: 400px;
    }
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  .mission-challenges {
    grid-template-columns: 1fr;
  }
  
  .capability-grid {
    grid-template-columns: 1fr;
  }
  
  .scenario-grid {
    grid-template-columns: 1fr;
  }
  
  .tech-architecture {
    flex-direction: column;
    
    .architecture-image {
      margin-bottom: 30px;
    }
  }
  
  .quality-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .comparison-cards {
    grid-template-columns: 1fr;
  }
  
  .module-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .case-cards {
    grid-template-columns: 1fr;
  }
  
  .pricing-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .cta-section {
    padding: 90px 0;
    
    .section-title {
      font-size: 38px;
    }
    
    .cta-subtitle {
      font-size: 19px;
      margin-bottom: 35px;
    }
    
    .cta-buttons {
      margin-bottom: 50px;
    }
    
    .cta-stats {
      gap: 30px;
      
      .cta-stat {
        .stat-number {
          font-size: 30px;
        }
        
        .stat-label {
          font-size: 15px;
        }
      }
    }
  }
}

// 手机设备 (576px - 767px)
@media (max-width: 767px) {
  .container {
    max-width: 540px;
    padding: 0 15px;
  }
  
  .hero {
    padding: 80px 0 60px;
    
    .hero-title {
      font-size: 36px;
    }
    
    .hero-subtitle {
      font-size: 20px;
    }
    
    .hero-image {
      width: 300px;
      height: 300px;
    }
    
    .hero-buttons {
      flex-direction: column;
      align-items: center;
      gap: 15px;
    }
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .mission-challenges {
    grid-template-columns: 1fr;
  }
  
  .capability-grid {
    grid-template-columns: 1fr;
  }
  
  .scenario-grid {
    grid-template-columns: 1fr;
  }
  
  .tech-architecture {
    flex-direction: column;
    
    .architecture-image {
      margin-bottom: 30px;
    }
  }
  
  .quality-grid {
    grid-template-columns: 1fr;
  }
  
  .comparison-cards {
    grid-template-columns: 1fr;
  }
  
  .module-grid {
    grid-template-columns: 1fr;
  }
  
  .case-cards {
    grid-template-columns: 1fr;
  }
  
  .pricing-cards {
    grid-template-columns: 1fr;
  }
  
  .cta-section {
    padding: 80px 0;
    
    .section-title {
      font-size: 32px;
    }
    
    .cta-subtitle {
      font-size: 18px;
      margin-bottom: 30px;
    }
    
    .cta-buttons {
      flex-direction: column;
      align-items: center;
      gap: 15px;
      margin-bottom: 40px;
      
      .cta-primary-btn,
      .cta-secondary-btn {
        width: 100%;
        max-width: 280px;
      }
    }
    
    .cta-stats {
      flex-direction: column;
      gap: 20px;
      
      .cta-stat {
        .stat-number {
          font-size: 28px;
        }
        
        .stat-label {
          font-size: 14px;
        }
      }
    }
  }
}

// 小屏手机设备 (575px 以下)
@media (max-width: 575px) {
  .container {
    padding: 0 10px;
  }
  
  .hero {
    padding: 60px 0 40px;
    
    .hero-title {
      font-size: 30px;
      line-height: 1.2;
    }
    
    .hero-subtitle {
      font-size: 18px;
    }
    
    .hero-image {
      width: 250px;
      height: 250px;
    }
  }
  
  .section {
    padding: 40px 0;
  }
  
  .section-title {
    font-size: 24px;
  }
  
  .section-subtitle {
    font-size: 14px;
  }
  
  .stats-item {
    padding: 15px;
    
    .stat-number {
      font-size: 24px;
    }
    
    .stat-label {
      font-size: 14px;
    }
  }
  
  .mission-card, .challenge-card {
    padding: 20px;
    
    h3 {
      font-size: 18px;
    }
    
    p {
      font-size: 14px;
    }
  }
  
  .capability-item, .scenario-item {
    padding: 20px;
    
    .item-icon {
      width: 50px;
      height: 50px;
      
      i {
        font-size: 24px;
      }
    }
    
    h3 {
      font-size: 18px;
    }
    
    p {
      font-size: 14px;
    }
  }
  
  .tech-architecture {
    .architecture-image {
      height: 150px;
    }
    
    .architecture-content {
      h3 {
        font-size: 18px;
      }
      
      p {
        font-size: 14px;
      }
    }
  }
  
  .quality-item {
    padding: 20px;
    
    .quality-icon {
      width: 50px;
      height: 50px;
      
      i {
        font-size: 24px;
      }
    }
    
    h3 {
      font-size: 18px;
    }
    
    p {
      font-size: 14px;
    }
  }
  
  .comparison-card {
    padding: 20px;
    
    h3 {
      font-size: 18px;
    }
    
    .comparison-metrics {
      .metric {
        font-size: 14px;
      }
    }
  }
  
  .module-item, .case-card {
    .module-icon, .case-icon {
      width: 60px;
      height: 60px;
      
      i {
        font-size: 28px;
      }
    }
    
    .module-content, .case-content {
      padding: 0 20px 20px;
      
      h3 {
        font-size: 18px;
      }
      
      p {
        font-size: 14px;
      }
    }
  }
  
  .pricing-card {
    .pricing-header {
      padding: 20px 20px 15px;
      
      h3 {
        font-size: 20px;
      }
      
      .price {
        font-size: 24px;
      }
    }
    
    .pricing-features {
      padding: 15px 20px;
      
      .feature-item {
        margin-bottom: 10px;
        
        span {
          font-size: 14px;
        }
      }
    }
    
    .pricing-action {
      padding: 0 20px 20px;
    }
  }
  
  .cta-section {
    padding: 40px 0;
    
    .section-title {
      font-size: 22px;
    }
    
    .cta-subtitle {
      font-size: 16px;
    }
    
    .cta-buttons {
      margin-bottom: 30px;
      
      .cta-primary-btn,
      .cta-secondary-btn {
        padding: 12px 20px;
        font-size: 16px;
      }
    }
    
    .cta-stats {
      gap: 15px;
      
      .cta-stat {
        .stat-number {
          font-size: 24px;
        }
        
        .stat-label {
          font-size: 12px;
        }
      }
    }
  }
}
</style>